// react
import React, { createContext, useContext, useEffect, useMemo } from 'react';

// posthog
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';

// models
import { Account } from '@karehero/models';

const POSTHOG_HOST = import.meta.env.REACT_APP_POSTHOG_HOST as string;
const POSTHOG_KEY = import.meta.env.REACT_APP_POSTHOG_KEY as string;

interface PostHogContextValue {
  isReady: boolean;
}

const PostHogContext = createContext<PostHogContextValue>({ isReady: false });

const isPostHogEnabled = !!POSTHOG_HOST && !!POSTHOG_KEY;

// POSTHOG_HOST is the same-origin path nginx proxies to PostHog (`/rp`, see
// front-end/pwa/nginx.conf) so ad blockers don't block flags and events.
// posthog-js derives the flags endpoint from api_host. ui_host keeps the
// toolbar working, as it can no longer be derived from a proxy path.
const POSTHOG_UI_HOST = 'https://eu.posthog.com';

if (isPostHogEnabled) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
  });
}

interface PostHogProps {
  children: React.ReactNode;
}

export const PostHog = ({ children }: PostHogProps) => {
  const contextValue = useMemo(() => ({ isReady: isPostHogEnabled }), []);

  useEffect(() => {
    if (!isPostHogEnabled) return;

    const interval = setInterval(() => {
      posthog.reloadFeatureFlags();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  if (!isPostHogEnabled) {
    return (
      <PostHogContext.Provider value={contextValue}>
        {children}
      </PostHogContext.Provider>
    );
  }

  return (
    <PHProvider client={posthog}>
      <PostHogContext.Provider value={contextValue}>
        {children}
      </PostHogContext.Provider>
    </PHProvider>
  );
};

export const usePostHogContext = () => useContext(PostHogContext);

export const identifyUser = (account: Account) => {
  if (!isPostHogEnabled || !account) return;

  const properties: Record<string, string | undefined> = {
    email: account.email,
    name: `${account.firstName} ${account.lastName}`.trim(),
    kindeEmail: account.kindeEmail,
  };

  if (account.organization) {
    properties.organizationId = account.organization.id;
    properties.organizationName = account.organization.name;
    if (account.organization.partnerId) {
      properties.referringPartnerId = account.organization.partnerId;
    }
    if (account.organization.partnerName) {
      properties.referringPartnerName = account.organization.partnerName;
    }
  }

  posthog.identify(account.id, properties);

  if (account.organization) {
    posthog.group('organization', account.organization.id, {
      name: account.organization.name,
    });
  }
};

export const resetUser = () => {
  if (!isPostHogEnabled) return;
  posthog.reset();
};
